import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { prospectService } from '../services/api';
import { Scholarship } from '../types';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

const emptyForm = {
  title: '',
  amount: '',
  eligibility: '',
  deadline: '',
  description: '',
  benefits: [''],
  requirements: [''],
  is_active: true,
};

const ScholarshipsManagement: React.FC = () => {
  const { user } = useAuth();
  const [scholarships, setScholarships] = useState<Scholarship[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Scholarship | null>(null);
  const [form, setForm] = useState({ ...emptyForm });

  useEffect(() => {
    if (user?.is_admin) fetchScholarships();
  }, [user]);

  const fetchScholarships = async () => {
    setLoading(true);
    try {
      const data = await prospectService.getScholarships();
      setScholarships(data);
    } catch (e) {
      setError('Failed to load scholarships');
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value }));
  };

  const handleArrayChange = (field: 'benefits' | 'requirements', idx: number, value: string) => {
    setForm(f => {
      const arr = [...(f[field] as string[])];
      arr[idx] = value;
      return { ...f, [field]: arr };
    });
  };

  const addArrayItem = (field: 'benefits' | 'requirements') => {
    setForm(f => ({ ...f, [field]: [...(f[field] as string[]), ''] }));
  };

  const removeArrayItem = (field: 'benefits' | 'requirements', idx: number) => {
    setForm(f => ({ ...f, [field]: (f[field] as string[]).filter((_, i) => i !== idx) }));
  };

  const resetForm = () => {
    setForm({ ...emptyForm });
    setEditing(null);
    setShowForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editing) {
        await prospectService.updateScholarship(editing.id, form);
      } else {
        await prospectService.createScholarship(form);
      }
      fetchScholarships();
      resetForm();
    } catch {
      setError('Failed to save scholarship');
    }
  };

  const handleEdit = (sch: Scholarship) => {
    setEditing(sch);
    setForm({
      title: sch.title || '',
      amount: sch.amount || '',
      eligibility: sch.eligibility || '',
      deadline: sch.deadline || '',
      description: sch.description || '',
      benefits: sch.benefits || [''],
      requirements: sch.requirements || [''],
      is_active: sch.is_active,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this scholarship?')) return;
    try {
      await prospectService.deleteScholarship(id);
      fetchScholarships();
    } catch {
      setError('Failed to delete scholarship');
    }
  };

  if (!user?.is_admin) return <div className="text-center py-20 text-red-400">Admin access required.</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Scholarships Management</h1>
        <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">Track scholarship offerings and eligibility rules.</p>
      </div>
      {error && <div className="text-red-500 mb-4">{error}</div>}
      <button className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-4 py-2" onClick={() => { setShowForm(true); setEditing(null); setForm({ ...emptyForm }); }}>
        <PlusIcon className="w-5 h-5 mr-2" /> Add Scholarship
      </button>
      {showForm && (
        <form className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden mb-8" onSubmit={handleSubmit}>
          <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">{editing ? 'Edit scholarship' : 'Create scholarship'}</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input name="title" value={form.title} onChange={handleInput} placeholder="Title" className="border p-2 rounded" required />
            <input name="amount" value={form.amount} onChange={handleInput} placeholder="Amount" className="border p-2 rounded" />
            <input name="eligibility" value={form.eligibility} onChange={handleInput} placeholder="Eligibility" className="border p-2 rounded" />
            <input name="deadline" value={form.deadline} onChange={handleInput} placeholder="Deadline" className="border p-2 rounded" />
            <input name="is_active" type="checkbox" checked={form.is_active} onChange={handleInput} className="mr-2" /> Active
            </div>
            <textarea name="description" value={form.description} onChange={handleInput} placeholder="Description" className="border p-2 rounded w-full mt-2" />
            <div className="mt-2">
              <label className="font-semibold">Benefits:</label>
              {form.benefits.map((b, i) => (
                <div key={i} className="flex items-center mb-1">
                  <input value={b} onChange={e => handleArrayChange('benefits', i, e.target.value)} className="border p-2 rounded flex-1" />
                  <button type="button" onClick={() => removeArrayItem('benefits', i)} className="ml-2 text-red-500">Remove</button>
                </div>
              ))}
              <button type="button" onClick={() => addArrayItem('benefits')} className="text-blue-500 mt-1">Add Benefit</button>
            </div>
            <div className="mt-2">
              <label className="font-semibold">Requirements:</label>
              {form.requirements.map((r, i) => (
                <div key={i} className="flex items-center mb-1">
                  <input value={r} onChange={e => handleArrayChange('requirements', i, e.target.value)} className="border p-2 rounded flex-1" />
                  <button type="button" onClick={() => removeArrayItem('requirements', i)} className="ml-2 text-red-500">Remove</button>
                </div>
              ))}
              <button type="button" onClick={() => addArrayItem('requirements')} className="text-blue-500 mt-1">Add Requirement</button>
            </div>
            <div className="mt-4 flex gap-2">
              <button type="submit" className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-4 py-2">{editing ? 'Update' : 'Create'}</button>
              <button type="button" className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-4 py-2" onClick={resetForm}>Cancel</button>
            </div>
          </div>
        </form>
      )}
      {loading ? <div>Loading...</div> : (
        <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Scholarships</h3>
            <p className="text-xs text-gray-500">{scholarships.length} total</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200 dark:divide-slate-700/60">
              <thead className="bg-gray-50 dark:bg-slate-900">
                <tr>
                  <th className="p-2 text-left text-gray-900 dark:text-slate-100">Title</th>
                  <th className="p-2 text-left text-gray-900 dark:text-slate-100">Amount</th>
                  <th className="p-2 text-left text-gray-900 dark:text-slate-100">Eligibility</th>
                  <th className="p-2 text-left text-gray-900 dark:text-slate-100">Deadline</th>
                  <th className="p-2 text-left text-gray-900 dark:text-slate-100">Active</th>
                  <th className="p-2 text-gray-900 dark:text-slate-100">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-slate-700/60">
                {scholarships.map(sch => (
                  <tr key={sch.id}>
                    <td className="p-2">{sch.title}</td>
                    <td className="p-2">{sch.amount}</td>
                    <td className="p-2">{sch.eligibility}</td>
                    <td className="p-2">{sch.deadline}</td>
                    <td className="p-2">{sch.is_active ? 'Yes' : 'No'}</td>
                    <td className="p-2 flex gap-2">
                      <button onClick={() => handleEdit(sch)} className="text-blue-600"><PencilIcon className="w-5 h-5" /></button>
                      <button onClick={() => handleDelete(sch.id)} className="text-red-600"><TrashIcon className="w-5 h-5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScholarshipsManagement;
